export interface Master {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  categories?: string[]; // Категорії бізнесу (виступають як спеціальності)
  rating?: number;
  experience?: string;
  about?: string;
  businessName?: string;
  businessAddress?: string;
  businessPhone?: string;
  avatar?: string;
  isActive?: boolean;
  city?: string;
}

export interface MastersResponse {
  masters: Master[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

export interface GetMastersOptions {
  city?: string;
  page?: number;
  limit?: number;
  search?: string;
  signal?: AbortSignal;
  language?: string;
}

/**
 * Отримання списку майстрів (використовує бізнеси з прапорцем is_master_business)
 */
export const getMasters = async (
  city?: string,
  page: number = 1,
  limit: number = 25,
  options: GetMastersOptions = {}
): Promise<MastersResponse> => {
  const { search, signal, language } = options;
  
  const params = new URLSearchParams();
  
  if (city) {
    params.append('city', city);
  }
  
  if (page) {
    params.append('page', page.toString());
  }
  
  if (limit) {
    params.append('limit', limit.toString());
  }
  
  if (search) {
    params.append('search', search);
  }

  if (language) {
    params.append('lang', language);
  }

  // Використовуємо API бізнесів з фільтрацією по is_master_business=true
  const url = `/api/v1/businesses${params.toString() ? `?${params.toString()}` : ''}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Фільтруємо тільки бізнеси де is_master_business = true
    const masterBusinesses = (data.businesses || []).filter((business: any) => business.is_master_business);
    
    // Конвертуємо бізнеси-майстрів в майстрів
    const masters: Master[] = masterBusinesses.map((business: any) => ({
      id: business.id,
      firstName: business.owner_name || 'Master',
      lastName: business.name?.split(' ')[1] || 'Professional',
      fullName: business.owner_name || business.name || 'Master Professional',
      email: business.email,
      phone: business.phone,
      categories: business.categories || [], // Категорії бізнесу (виступають як спеціальності)
      rating: business.rating,
      experience: business.experience || '5+ years', // Використовуємо досвід з API
      about: business.description || 'Professional master with extensive experience',
      businessName: business.name,
      businessAddress: business.address,
      businessPhone: business.phone,
      avatar: business.image, // Виправлено: logo_url -> image
      isActive: business.is_active,
      city: business.city || city,
    }));
    
    return {
      masters,
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || page,
      totalItems: data.totalItems || masters.length,
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    
    console.error('Error fetching masters:', error);
    throw error;
  }
};

/**
 * Отримання майстра за ID (використовує бізнеси з прапорцем is_master_business)
 */
export const getMasterById = async (id: string): Promise<Master | null> => {
  try {
    // Використовуємо API бізнесів
    const response = await fetch(`/api/v1/businesses/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const business = await response.json();
    
    // Перевіряємо чи це бізнес-майстр
    if (!business.is_master_business) {
      return null; // Це не майстр, а звичайний бізнес
    }
    
    // Конвертуємо бізнес-майстра в майстра
    const master: Master = {
      id: business.id,
      firstName: business.owner_name || 'Master',
      lastName: business.name?.split(' ')[1] || 'Professional',
      fullName: business.owner_name || business.name || 'Master Professional',
      email: business.email,
      phone: business.phone,
      categories: business.categories || [], // Категорії бізнесу (виступають як спеціальності)
      rating: business.rating,
      experience: business.experience || '5+ years', // Використовуємо досвід з API
      about: business.description || 'Professional master with extensive experience',
      businessName: business.name,
      businessAddress: business.address,
      businessPhone: business.phone,
      avatar: business.image, // Виправлено: logo_url -> image
      isActive: business.is_active,
      city: business.city,
    };

    return master;
  } catch (error) {
    console.error('Error fetching master by ID:', error);
    throw error;
  }
};
